/**
 * Per-shell navigation policy hook.
 *
 * Centralises every "is this nav item appropriate on this shell?"
 * decision so Header, AccountMenu, and post-signup redirects don't
 * each call ``resolveShell()`` and drift apart over time. The shape
 * is intentionally small — booleans for visibility plus a typed
 * primaryLinks array — so callers stay declarative.
 *
 * Why per-shell at all: the marketing shell (forgmatic.com) does NOT
 * mount tenant routes (``/cart``, ``/materials``, ``/services/:slug``,
 * ``/account``…), so rendering links to them would 404 by design.
 * The tenant shell does NOT mount SaaS-marketing routes (``/for-providers``,
 * ``/for-business``, ``/pricing-plans``) — its primaryLinks list omits
 * them. The platform-admin shell (admin.forgmatic.com) renders only
 * the cross-tenant admin console — neither buyer nav nor SaaS
 * marketing applies.
 */

import {
  buildTenantUrl,
  defaultTenantHost,
  resolveShell,
  type ShellKind,
} from "@/shell/resolveShell";
import { PUBLIC_PAGES, type PublicPageKey } from "@/shell/toggleablePages";

export type AccountMenuVariant = "tenant" | "platform-slim" | "platform-admin";

export interface PrimaryLink {
  /** href without the lang prefix — caller composes ``/${lang}${to}``. */
  to: string;
  /** i18n key under ``common:nav``. */
  i18nKey:
    | "howItWorks"
    | "materials"
    | "pricing"
    | "contact"
    | "forBusiness"
    | "plans";
  /** ``data-testid`` so the e2e suite can target by purpose. */
  testId: string;
  /**
   * Tenant-scoped page visibility key. Present only on links that
   * sit on the tenant shell — Header/Footer filter on this against
   * the per-tenant ``pages_visibility`` map.
   */
  pageKey?: PublicPageKey;
}

export interface ShellNav {
  shell: ShellKind;
  primaryLinks: readonly PrimaryLink[];
  showCart: boolean;
  showServicesDropdown: boolean;
  /**
   * The „Co umíme" dropdown — PLATFORM SHELL ONLY (KAN-1462).
   *
   * This reverses, for this one entry, the decision in #4693 that the
   * header and footer stay as they are. The owner made the exception on
   * 2026-09-18 and it is narrow on purpose: the section is five pages,
   * and five flat entries would push the marketing nav past the point
   * where anyone reads it.
   *
   * ⚠️ Deliberately false on the tenant shell. A tenant storefront has
   * no ``/features`` routes — the section is the SaaS pitch, mounted on
   * ``forgmatic.com`` only — so the entry would link a buyer to a 404
   * and tell them their supplier runs on somebody else's platform. Same
   * reasoning as ``/changelog`` in the footer.
   */
  showFeaturesDropdown: boolean;
  accountMenuVariant: AccountMenuVariant;
  /** Cross-host URL builder — see ``buildTenantUrl`` for semantics. */
  buildTenantUrl: (path: string, slug?: string | null) => string;
  defaultTenantHost: string;
}

// Derived from the public page registry (KAN-1542): which pages sit in
// the header, and in what order, is decided there.
const TENANT_PRIMARY_LINKS: readonly PrimaryLink[] = PUBLIC_PAGES.flatMap(
  (page) =>
    "header" in page
      ? [
          {
            to: `/${page.path}`,
            i18nKey: page.header.i18nKey,
            testId: page.header.testId,
            pageKey: page.key,
          },
        ]
      : [],
);

// On the platform shell "Pricing" deep-links to the SaaS-plans page
// (``/pricing-plans``), since per-tenant material pricing has no
// canonical destination here. Materials is dropped entirely — buyer
// concept with no marketing analogue.
//
// ``/for-providers`` is gone (KAN-908): it addressed the same shop
// owner as ``/`` in a different voice, with a different headline and a
// different number of steps, so the nav offered a visitor two doors
// into one room. Its six value cards moved onto the landing and the
// route now redirects there.
//
// ``/for-business`` stays out of the primary nav and lives in the
// footer. The buyer it addresses arrives through their SUPPLIER's shop,
// not through forgmatic.com, so the entry cost a slot in the
// acquisition path and bought nothing.
//
// ``/how-it-works`` comes BACK here, and the reason it left no longer
// holds. It was demoted when the platform host mounted the tenant's
// buyer-facing page — a visitor who came to buy the SaaS landed in a
// page written for the person ordering a part. KAN-717 replaced that
// with a platform version told from the shop's side ("co nastavíte vy,
// co uvidí váš zákazník, co přistane vám"), so the page now speaks to
// exactly the audience this nav serves.
const PLATFORM_PRIMARY_LINKS: readonly PrimaryLink[] = [
  { to: "/how-it-works", i18nKey: "howItWorks", testId: "nav-how-it-works" },
  { to: "/pricing-plans", i18nKey: "plans", testId: "nav-pricing" },
  { to: "/contact", i18nKey: "contact", testId: "nav-contact" },
];

// admin.forgmatic.com renders AdminLayout's own sidebar; the marketing
// PrimaryNav has nothing useful to surface there.
const ADMIN_PRIMARY_LINKS: readonly PrimaryLink[] = [];

export function useShellNav(): ShellNav {
  const shell = resolveShell();
  switch (shell) {
    case "platform":
      return {
        shell,
        primaryLinks: PLATFORM_PRIMARY_LINKS,
        showCart: false,
        showServicesDropdown: false,
        showFeaturesDropdown: true,
        accountMenuVariant: "platform-slim",
        buildTenantUrl,
        defaultTenantHost: defaultTenantHost(),
      };
    case "platform-admin":
      return {
        shell,
        primaryLinks: ADMIN_PRIMARY_LINKS,
        showCart: false,
        showServicesDropdown: false,
        showFeaturesDropdown: false,
        accountMenuVariant: "platform-admin",
        buildTenantUrl,
        defaultTenantHost: defaultTenantHost(),
      };
    case "tenant":
    default:
      return {
        shell: "tenant",
        primaryLinks: TENANT_PRIMARY_LINKS,
        showCart: true,
        showServicesDropdown: true,
        showFeaturesDropdown: false,
        accountMenuVariant: "tenant",
        buildTenantUrl,
        defaultTenantHost: defaultTenantHost(),
      };
  }
}
